"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  alpha,
  Alert,
  Fade,
  useTheme,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import CategoryIcon from "@mui/icons-material/Category"
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight"
import RefreshIcon from "@mui/icons-material/Refresh"
import FolderIcon from "@mui/icons-material/Folder"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import TablaSubCategoriaComponent from "./TablaSubCategoriaTransaccionComponent.js"

const GestionCategoriaUnificadaComponent = () => {
  const theme = useTheme()
  const {
    categoriaTransaccion,
    getCategoriaTransaccion,
    postCategoriaTransaccion,
    postSubCategoriaTransaccion,
    updateCategoriaTransaccion,
    deleteCategoriaTransaccion,
    isNavbarCollapsed,
  } = useContext(ErpContext)

  // Estados para categorías
  const [descripcionCategoriaTransaccion, setDescripcionCategoriaTransaccion] = useState("")

  // Estados para subcategorías
  const [descripcionSubCategoriaTransaccion, setDescripcionSubCategoriaTransaccion] = useState("")
  const [idCategoriaTransaccion, setIdCategoriaTransaccion] = useState("")

  // Estados generales
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState("")
  const [editingCategoriaId, setEditingCategoriaId] = useState(null)
  const [editingCategoriaDescripcion, setEditingCategoriaDescripcion] = useState("")

  useEffect(() => {
    fetchCategorias()
  }, [])

  const fetchCategorias = async () => {
    setLoading(true)
    try {
      await getCategoriaTransaccion()
    } catch (err) {
      console.error("Error al cargar categorías:", err)
      setError("No se pudieron cargar las categorías. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleCategoriaSubmit = async (e) => {
    e.preventDefault()
    if (!descripcionCategoriaTransaccion.trim()) {
      setError("Por favor ingrese una descripción para la categoría")
      return
    }

    setLoading(true)
    try {
      await postCategoriaTransaccion(descripcionCategoriaTransaccion)
      setDescripcionCategoriaTransaccion("")
      setSuccessMessage("Categoría agregada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      await fetchCategorias() // Actualizar la lista
    } catch (err) {
      console.error("Error al agregar categoría:", err)
      setError("No se pudo agregar la categoría. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleCategoriaEditStart = (categoria) => {
    setError(null)
    setEditingCategoriaId(categoria.id)
    setEditingCategoriaDescripcion(categoria.descripcion)
  }

  const handleCategoriaEditCancel = () => {
    setEditingCategoriaId(null)
    setEditingCategoriaDescripcion("")
  }

  const handleCategoriaEditSave = async () => {
    if (!editingCategoriaDescripcion.trim()) {
      setError("Por favor ingrese una descripción para la categoría")
      return
    }

    setLoading(true)
    try {
      await updateCategoriaTransaccion(editingCategoriaId, editingCategoriaDescripcion.trim())
      setSuccessMessage("Categoría actualizada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      handleCategoriaEditCancel()
    } catch (err) {
      console.error("Error al actualizar categoría:", err)
      setError("No se pudo actualizar la categoría. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleCategoriaDelete = async (categoriaId) => {
    const confirmDelete = window.confirm("¿Está seguro de eliminar esta categoría? Se eliminarán también sus subcategorías asociadas.")
    if (!confirmDelete) return

    setLoading(true)
    try {
      await deleteCategoriaTransaccion(categoriaId)
      setSuccessMessage("Categoría eliminada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al eliminar categoría:", err)
      setError("No se pudo eliminar la categoría. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleCategoriaEditKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleCategoriaEditSave()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      handleCategoriaEditCancel()
    }
  }

  const handleSubCategoriaSubmit = async (e) => {
    e.preventDefault()
    if (!descripcionSubCategoriaTransaccion.trim() || !idCategoriaTransaccion) {
      setError("Por favor complete todos los campos para la subcategoría")
      return
    }

    setLoading(true)
    try {
      await postSubCategoriaTransaccion(descripcionSubCategoriaTransaccion, idCategoriaTransaccion)
      setDescripcionSubCategoriaTransaccion("")
      setIdCategoriaTransaccion("")
      setSuccessMessage("Subcategoría agregada correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar subcategoría:", err)
      setError("No se pudo agregar la subcategoría. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Encabezado principal */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 3,
          backgroundColor: "#5b5b5f",
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.9),
          },
          color: "white",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <CategoryIcon
              sx={{
                fontSize: 32,
                color: "rgb(244, 177, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: "white", }}>
              Gestión de Categorías y Subcategorías
            </Typography>
          </Box>
          <Typography variant="body1" color="white" sx={{ ml: 6 }}>
            Administre las categorías principales y sus subcategorías asociadas
          </Typography>
        </CardContent>
      </Card>

      {/* Alertas */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 2,
          }}
          onClose={() => setSuccess(false)}
        >
          {successMessage}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 2,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sección de Categorías */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              height: "fit-content",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <FolderIcon
                  sx={{
                    fontSize: 24,
                    color: "rgb(244, 177, 14)",
                    mr: 1.5,
                  }}
                />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Crear Categoría Principal
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <form onSubmit={handleCategoriaSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción de Categoría"
                      variant="outlined"
                      fullWidth
                      value={descripcionCategoriaTransaccion}
                      onChange={(e) => setDescripcionCategoriaTransaccion(e.target.value)}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CategoryIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={<AddCircleOutlineIcon />}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: 2,
                        py: 1.5,
                        backgroundColor: "#5b5b5f",
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Agregar Categoría
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {/* Lista de categorías existentes */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#2d3748" }}>
                  Categorías Existentes ({categoriaTransaccion.length})
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                  <List dense>
                    {categoriaTransaccion.map((categoria) => (
                      <ListItem
                        key={categoria.id}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          backgroundColor: "#f8f9fa",
                          "&:hover": {
                            backgroundColor: "#E3EAFD",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <FolderIcon sx={{ color: "rgb(244, 177, 14)", fontSize: 20 }} />
                        </ListItemIcon>
                        <Box sx={{ flexGrow: 1, mr: 2 }}>
                          {editingCategoriaId === categoria.id ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={editingCategoriaDescripcion}
                              onChange={(e) => setEditingCategoriaDescripcion(e.target.value)}
                              onKeyDown={handleCategoriaEditKeyDown}
                              disabled={loading}
                              autoFocus
                            />
                          ) : (
                            <ListItemText
                              primary={categoria.descripcion}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                fontSize: "0.95rem",
                              }}
                            />
                          )}
                        </Box>
                        <Chip
                          label={`ID: ${categoria.id}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#5b5b5f", 0.1),
                            color: "#5b5b5f",
                            fontSize: "0.75rem",
                          }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 0.5 }}>
                          {editingCategoriaId === categoria.id ? (
                            <>
                              <Tooltip title="Guardar cambios">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={handleCategoriaEditSave}
                                    disabled={loading}
                                    sx={{
                                      color: "#2e7d32",
                                      bgcolor: alpha("#2e7d32", 0.1),
                                      "&:hover": {
                                        bgcolor: alpha("#2e7d32", 0.2),
                                      },
                                    }}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Cancelar">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={handleCategoriaEditCancel}
                                    disabled={loading}
                                    sx={{
                                      color: "#6b6b6b",
                                      bgcolor: alpha("#6b6b6b", 0.1),
                                      "&:hover": {
                                        bgcolor: alpha("#6b6b6b", 0.2),
                                      },
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Editar categoría">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCategoriaEditStart(categoria)}
                                    disabled={loading}
                                    sx={{
                                      color: "rgb(244, 177, 14)",
                                      bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                                      "&:hover": {
                                        bgcolor: alpha("rgb(244, 177, 14)", 0.2),
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Eliminar categoría">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCategoriaDelete(categoria.id)}
                                    disabled={loading}
                                    sx={{
                                      color: "#d32f2f",
                                      bgcolor: alpha("#d32f2f", 0.1),
                                      "&:hover": {
                                        bgcolor: alpha("#d32f2f", 0.2),
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sección de Subcategorías */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              height: "fit-content",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SubdirectoryArrowRightIcon
                  sx={{
                    fontSize: 24,
                    color: "rgb(244, 177, 14)",
                    mr: 1.5,
                  }}
                />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Crear Subcategoría
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <form onSubmit={handleSubCategoriaSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción de Subcategoría"
                      variant="outlined"
                      fullWidth
                      value={descripcionSubCategoriaTransaccion}
                      onChange={(e) => setDescripcionSubCategoriaTransaccion(e.target.value)}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SubdirectoryArrowRightIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="categoria-label">Categoría Principal</InputLabel>
                      <Select
                        labelId="categoria-label"
                        value={idCategoriaTransaccion}
                        onChange={(e) => setIdCategoriaTransaccion(e.target.value)}
                        label="Categoría Principal"
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryIcon color="action" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="" disabled>
                          Seleccione Categoría
                        </MenuItem>
                        {categoriaTransaccion.map((categoria) => (
                          <MenuItem key={categoria.id} value={categoria.id}>
                            {categoria.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={<AddCircleOutlineIcon />}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: 2,
                        py: 1.5,
                        backgroundColor: "#5b5b5f",
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Agregar Subcategoría
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de subcategorías */}
      <Card
        elevation={2}
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SubdirectoryArrowRightIcon
              sx={{
                fontSize: 24,
                color: "rgb(244, 177, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Subcategorías de Transacción
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={fetchCategorias}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 120,
                borderColor: "rgb(244, 177, 14)",
                color: "rgb(244, 177, 14)",
                "&:hover": {
                  borderColor: alpha("rgb(244, 177, 14)", 0.8),
                  backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
                },
              }}
            >
              Actualizar
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TablaSubCategoriaComponent />
        </CardContent>
      </Card>
    </Box>
  )
}

export default GestionCategoriaUnificadaComponent
